import { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, Button, Flex, Spinner } from '@smarterservices/smarter-ui';
import DraggableModal from '@ui/shared/components/Common/DraggableModal/DraggableModal';
import { useExamSessionStore, useAppStore, getStore } from '../../../stores/StoreContext';
import RemoteControlIframe from './RemoteControlIframe';
import StatusOverlays from './StatusOverlays';

/**
 * RemoteControlModal — Orchestrator for the remote control / co-browsing feature.
 *
 * Manages all local state (connection status, errors, loading flags) and event handlers,
 * then delegates rendering to child components:
 * - {@link RemoteControlIframe} — Upscope iframe + sidebar with tool cards
 * - {@link StatusOverlays} — Status-specific overlays (intro, connecting, waiting, terminated)
 *
 * The iframe is always mounted (hidden) once a watchUrl is available to maintain
 * the postMessage communication channel with Upscope. Status overlays are absolutely
 * positioned on top of the iframe and shown/hidden based on the current session state.
 *
 * Additionally handles:
 * - Showing a "Lockdown & Start" tool when ExamLockdownAndStart hasn't been completed
 * - Showing a confirmation warning on close when ExamLockdownAndStart has been started but not completed
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @returns {JSX.Element} The RemoteControlModal
 */
function RemoteControlModal({ isOpen }) {
  const uniqueId = useExamSessionStore((state) => state.remoteControl?.uniqueId);
  const watchUrl = useExamSessionStore((state) => state.remoteControl?.watchUrl);
  const journeySteps = useExamSessionStore((state) => state.journey?.steps);
  const isRoomConnected = useAppStore((state) => state.isRoomConnected);
  const ExamSessionStore = getStore('examSessionStore');
  const AppStore = getStore('appStore');
  const [isStarting, setIsStarting] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [remoteControlStatus, setRemoteControlStatus] = useState(null);
  const [error, setError] = useState(null);
  const [isCloseConfirmVisible, setIsCloseConfirmVisible] = useState(false);
  const [isLockingDown, setIsLockingDown] = useState(false);

  /** Whether the iframe should be visible — only when the session is active or viewerReady */
  const isIframeVisible = !!(watchUrl && (remoteControlStatus === 'active' || remoteControlStatus === 'viewerReady'));

  /** ExamRedirect must be completed before lockdown is even eligible */
  const isRedirectCompleted = useMemo(() => {
    if (!journeySteps?.length) return false;
    return journeySteps.some(
      (step) => step.stepId === 'ExamRedirect' && step.type === 'standard' && step.completedTime !== null
    );
  }, [journeySteps]);

  /** Whether ExamLockdownAndStart has been completed in the journey (only relevant if redirect is done) */
  const isLockdownCompleted = useMemo(() => {
    if (!isRedirectCompleted) return true; // treat as "completed" so the card/confirmation are hidden
    if (!journeySteps?.length) return false;
    return journeySteps.some(
      (step) => step.stepId === 'ExamLockdownAndStart' && step.type === 'standard' && step.completedTime !== null
    );
  }, [journeySteps, isRedirectCompleted]);

  /** Whether ExamLockdownAndStart has been started but not yet completed (only relevant if redirect is done) */
  const isLockdownInProgress = useMemo(() => {
    if (!isRedirectCompleted) return false;
    if (!journeySteps?.length) return false;
    return journeySteps.some(
      (step) => step.stepId === 'ExamLockdownAndStart' && step.type === 'standard' && step.startTime !== null && step.completedTime === null
    );
  }, [journeySteps, isRedirectCompleted]);

  /**
   * Invokes the ExamLockdownAndStart extension action on the test taker's browser.
   *
   * @returns {Promise<void>}
   */
  const handleLockdownAndStart = async () => {
    try {
      await ExamSessionStore.invokeAction({ action: 'ExamLockdownAndStart' });
    } catch (e) {
      console.error('[RemoteControl] ExamLockdownAndStart action failed:', e);
    }
  };

  /**
   * Handles the close intent. If ExamLockdownAndStart has been started but not
   * completed, shows a confirmation warning instead of closing immediately.
   *
   * @returns {Promise<void>}
   */
  const onClose = async () => {
    if (isLockdownInProgress) {
      setIsCloseConfirmVisible(true);
      return;
    }
    await performClose();
  };

  /**
   * Performs the actual close — resets all local state and delegates
   * to AppStore.closeRemoteControlModal() which ends the remote control
   * session via the API and clears the store state.
   *
   * @returns {Promise<void>}
   */
  const performClose = async () => {
    setIsCloseConfirmVisible(false);
    await AppStore.closeRemoteControlModal();
    setRemoteControlStatus(null);
    setError(null);
    setIsStarting(false);
    setIsReconnecting(false);
    setIsLockingDown(false);
  };

  /**
   * Invokes ExamLockdownAndStart from the close confirmation, then closes.
   *
   * @returns {Promise<void>}
   */
  const handleLockdownAndClose = async () => {
    setIsLockingDown(true);
    try {
      await ExamSessionStore.invokeAction({ action: 'startExamLockdownAndStart' });
    } catch (e) {
      console.error('[RemoteControl] ExamLockdownAndStart action failed:', e);
    }
    await performClose();
  };

  /**
   * Handles postMessage events from the Upscope co-browsing iframe.
   * Updates remoteControlStatus when a sessionStatus value is received.
   * Possible sessionStatus values: 'waiting', 'pendingRequest', 'active', 'viewerReady', 'terminated'.
   *
   * @param {MessageEvent} event - The postMessage event from the iframe
   */
  const handleIframeMessage = useCallback((event) => {
    console.log('[RemoteControl] postMessage received:', {
      origin: event.origin,
      data: event.data,
    });
    if (event?.data?.sessionStatus) {
      setRemoteControlStatus(event.data.sessionStatus);
    }
  }, []);

  /**
   * Callback fired when the Upscope iframe finishes loading.
   */
  const handleIframeLoad = useCallback(() => {
    console.log('[RemoteControl] iframe loaded successfully');
  }, []);

  /**
   * Sends a postMessage to the Upscope co-browsing iframe.
   * The iframe is accessed by DOM id ('upscope-iframe') and must be mounted for this to work.
   *
   * @param {Object} message - The message payload to send to the iframe
   */
  const postMessageToIframe = useCallback((message) => {
    const iframe = document.getElementById('upscope-iframe');
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(message, '*');
    } else {
      console.warn('[RemoteControl] Cannot post message — iframe not available');
    }
  }, []);

  // Register/unregister the postMessage listener for iframe communication
  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);
    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [handleIframeMessage]);

  // Cleanup on unmount — ends the remote control session via onClose
  useEffect(() => {
    return () => {
      onClose();
    };
  }, []);

  /**
   * Re-injects the remote control script to re-establish the co-browsing connection.
   * Used when the connection is lost, the page is refreshed, or the screen freezes.
   *
   * @returns {Promise<void>}
   */
  const handleReconnect = async () => {
    setIsReconnecting(true);
    try {
      await ExamSessionStore.injectRemoteControl(uniqueId);
    } catch (e) {
      console.error('[RemoteControl] Reconnect failed:', e);
    } finally {
      setIsReconnecting(false);
    }
  };

  /**
   * Initiates a new remote control session.
   * Generates a unique ID, injects the script, and polls the API for a watchUrl.
   * On failure, sets an error message displayed in the UI.
   *
   * @returns {Promise<void>}
   */
  const handleStartRemoteControl = async () => {
    setIsStarting(true);
    setError(null);
    try {
      const result = await ExamSessionStore.startRemoteControl();
      if (!result?.successful) {
        setError(result?.error || 'Failed to start remote control');
      }
    } catch (e) {
      setError(e.message || 'An unexpected error occurred');
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <DraggableModal
      isOpen={isOpen}
      onClose={onClose}
      title="Remote Control"
      initialSize={{ width: 900, height: 700 }}
      minSize={{ width: 600, height: 400 }}
      maxSize={{ width: '90%', height: '90%' }}
      showMinMax={true}
      resizable={true}
      minimizable={true}
      constrainToContainer={false}
      maintainPositionOnClose={true}
    >
      <div style={{ position: 'relative', height: '100%', width: '100%' }}>
        <RemoteControlIframe
          watchUrl={watchUrl}
          isVisible={isIframeVisible}
          onIframeLoad={handleIframeLoad}
          uniquePassword={ExamSessionStore.getUniqueExamPassword()}
          actualPassword={ExamSessionStore.sessionData?.examConfiguration?.password}
          onReconnect={handleReconnect}
          isReconnecting={isReconnecting}
          showLockdownTool={!isLockdownCompleted}
          onLockdownAndStart={handleLockdownAndStart}
        />
        <StatusOverlays
          isRoomConnected={isRoomConnected}
          uniqueId={uniqueId}
          watchUrl={watchUrl}
          remoteControlStatus={remoteControlStatus}
          onStart={handleStartRemoteControl}
          isStarting={isStarting}
          error={error}
          onReconnect={handleReconnect}
          isReconnecting={isReconnecting}
        />
        {isCloseConfirmVisible && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 10,
          }}>
            <View
              as="div"
              background="primary"
              borderRadius="large"
              padding="large"
              maxWidth="420px"
              shadow="topmost"
            >
              <Flex direction="column" gap="medium">
                <Text weight="bold" size="large">
                  Exam Not Locked Down
                </Text>
                <Text size="small">
                  The test taker is not currently in lockdown mode. If you unlocked
                  the exam while remote controlling, you must lock down the session before
                  exiting. Click the button below to lockdown,
                  or close without locking down.
                </Text>
                <Flex direction="column" gap="small">
                  <Button
                    color="primary"
                    display="block"
                    onClick={handleLockdownAndClose}
                    interaction={isLockingDown ? 'disabled' : 'enabled'}
                  >
                    {isLockingDown ? (
                      <Flex alignItems="center" gap="x-small" justifyItems="center">
                        <Spinner size="x-small" renderTitle="Locking down" />
                        <Text size="small">Locking Down...</Text>
                      </Flex>
                    ) : (
                      'Lockdown and Close'
                    )}
                  </Button>
                  <Button
                    color="secondary"
                    display="block"
                    onClick={performClose}
                    interaction={isLockingDown ? 'disabled' : 'enabled'}
                  >
                    Close Without Locking Down
                  </Button>
                  <Button
                    display="block"
                    onClick={() => setIsCloseConfirmVisible(false)}
                    interaction={isLockingDown ? 'disabled' : 'enabled'}
                  >
                    Cancel
                  </Button>
                </Flex>
              </Flex>
            </View>
          </div>
        )}
      </div>
    </DraggableModal>
  );
}

export default RemoteControlModal;
